import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Input as AntdInput } from 'antd'
import cls from 'classnames'
import styled from 'styled-components'

import withValidation from '../inputs/withValidation'
import LabelText from '../LabelText'

import { ClearIcon, StyledFieldWrapper } from './Inputs/InputWrapperBase'
import { Close, View, ViewOff } from './Icons'

export const VARIANTS = {
  OUTLINE: 'outline',
  FILLED: 'filled',
  REMOVABLE: 'removable',
}

const ICON_SIZE = '16px'

// Input types that support setSelectionRange (email, number, date, time, etc. don't support it)
const INPUT_TYPES_THAT_SUPPORT_SELECTION = new Set(['text', 'password', 'search', 'tel', 'url'])

const InputWrapper = styled(StyledFieldWrapper)`
  .ant-input-affix-wrapper {
    padding: 0 12px;
  }

  .ant-input {
    padding-right: 24px !important;
    &:-internal-autofill-selected {
      color: var(--ds-gray-1) !important;
    }
    &[type='password']:not(:placeholder-shown) {
      font-size: 22px !important;
      font-family: serif;
      letter-spacing: 3px;
    }
  }

  .ant-input-group-addon {
    padding: 0 7px;
    border-color: ${({ disabled, readOnly }) =>
      disabled || readOnly ? 'var(--ds-blue-6)' : 'var(--ds-blue-4)'};
    background-color: var(--color-surface) !important;

    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }
  }

  .ant-input-suffix {
    z-index: 1;
    margin-inline-start: -16px;
  }

  input {
    min-width: 160px;
    height: 30px;
  }

  input,
  .ant-input-affix-wrapper {
    font-size: var(--body-text);
    padding: 0 12px;
    background-color: ${({ variant }) =>
      variant === VARIANTS.FILLED ? 'var(--ds-blue-8)' : 'var(--color-surface)'} !important;
  }

  .ant-input:-webkit-autofill,
  .ant-input:-webkit-autofill:hover,
  .ant-input:-webkit-autofill:focus,
  .ant-input:-webkit-autofill:active {
    /* The main trick: a large inset box-shadow to cover the default autofill background */
    /* Use a matching background color token here. */
    -webkit-box-shadow: 0 0 0px 1000px var(--color-surface) inset !important;

    /* Prevents the browser's autofill transition from showing. */
    /* The very long duration makes it appear instantaneous. */
    transition:
      background-color 9999s ease-out,
      -webkit-text-fill-color 9999s ease-out;

    /* Optionally, set the text color as well, as browsers might change it */
    /* Use a matching text color token here. */
    -webkit-text-fill-color: var(--color-text) !important;
  }

  /* For Firefox, which uses a different pseudo-class and often doesn't need the box-shadow trick */
  .ant-input:-moz-autofill {
    color: var(--color-text) !important; /* Your desired text color */
    background-color: var(--color-surface) !important; /* Your desired background color */
  }
`

const RemoveButton = styled.button`
  all: unset;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg path {
    fill: ${({ disabled }) => (disabled ? 'var(--ds-gray-5)' : 'var(--ds-gray-2)')};
  }
`

/**
 * @typedef {Object} InputProps
 * @property {string} [id]
 * @property {string} [className]
 * @property {string} [value]
 * @property {string} [defaultValue]
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} [onChange]
 * @property {() => void} [onRemove]
 * @property {Object} [meta]
 * @property {string} [meta.error]
 * @property {boolean} [meta.touched]
 * @property {string} [placeholder]
 * @property {string} [labelText]
 * @property {boolean} [disabled]
 * @property {boolean} [autoFocus]
 * @property {boolean} [readOnly]
 * @property {boolean} [required]
 * @property {string} [name]
 * @property {'text' | 'password'} [type]
 * @property {'outline' | 'filled' | 'removable'} [variant]
 * @property {(event: React.FocusEvent<HTMLInputElement>) => void} [onFocus]
 * @property {string | React.ReactElement} [notification]
 * @property {string | React.ReactElement} [LeftIcon]
 * @property {string | React.ReactElement} [RightIcon]
 * @property {React.CSSProperties} [style]
 * @property {string} [autoComplete]
 * @property {boolean} [preserveCursorPosition=false] - Whether to preserve cursor position during value changes (useful for redux-form Field components)
 */
const Input = forwardRef(
  (
    {
      id,
      value,
      variant = VARIANTS.OUTLINE,
      defaultValue,
      onChange,
      meta = {},
      placeholder,
      labelText,
      disabled,
      name,
      type = 'text',
      autoFocus = false,
      onFocus,
      notification,
      className,
      onRemove,
      LeftIcon,
      RightIcon,
      readOnly = false,
      style,
      required,
      autoComplete = 'off',
      preserveCursorPosition = false,
      ...otherProps
    },
    forwardedRef
  ) => {
    const [show, setShow] = useState(true)
    const [view, setView] = useState(false)
    const inputRef = useRef(null)
    useImperativeHandle(forwardedRef, () => inputRef.current)
    const isPasswordType = type === 'password'
    const actualType = isPasswordType && view ? 'text' : type

    const suffixIcon = useMemo(() => {
      if (!isPasswordType) {
        return RightIcon ? <RightIcon width={ICON_SIZE} height={ICON_SIZE} /> : null
      }

      return view ? (
        <ViewOff width={ICON_SIZE} height={ICON_SIZE} onClick={() => setView(false)} />
      ) : (
        <View width={ICON_SIZE} height={ICON_SIZE} onClick={() => setView(true)} />
      )
    }, [isPasswordType, view, RightIcon])

    // Preserve cursor position when value changes (controlled by preserveCursorPosition prop)
    const handleChange = useCallback(
      (event) => {
        if (onChange) {
          const canPreserveCursor =
            preserveCursorPosition && INPUT_TYPES_THAT_SUPPORT_SELECTION.has(actualType)
          if (canPreserveCursor) {
            // Store cursor position before calling onChange
            const cursorPosition = event.target.selectionStart
            onChange(event)

            // Restore cursor position after the next render
            setTimeout(() => {
              if (inputRef.current?.input) {
                inputRef.current.input.setSelectionRange(cursorPosition, cursorPosition)
              }
            }, 0)
          } else {
            // For regular usage, just call onChange directly
            onChange(event)
          }
        }
      },
      [onChange, preserveCursorPosition, actualType]
    )

    if (!show) {
      return null
    }

    return (
      <InputWrapper
        className={cls(className)}
        style={style}
        variant={variant}
        disabled={disabled}
        type={type}
        readOnly={readOnly}
      >
        {labelText && (
          <LabelText
            id={id}
            text={labelText}
            style={disabled && { color: 'var(--ds-gray-5)' }}
            notification={notification}
            disabled={disabled}
            required={required}
          />
        )}
        {(() => {
          // antd warns whenever the `addonBefore` prop key is present at all (even falsy),
          // so only spread it in for the removable variant to avoid a deprecation warning
          // on every other Input usage.
          const addonBeforeProps =
            variant === VARIANTS.REMOVABLE
              ? {
                  addonBefore: (
                    <RemoveButton
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        setShow(false)
                        if (onRemove) {
                          onRemove()
                        }
                      }}
                      id={`${id}-remove`}
                    >
                      <Close width="16px" height="16px" />
                    </RemoveButton>
                  ),
                }
              : {}

          const inputElement = (
            <AntdInput
              {...otherProps}
              {...addonBeforeProps}
              ref={inputRef}
              autoComplete={autoComplete}
              allowClear={{
                clearIcon: (
                  <ClearIcon
                    width="16px"
                    height="16px"
                    bgColor={variant === VARIANTS.FILLED ? 'var(--ds-blue-6)' : null}
                    bgHoverColor={variant === VARIANTS.FILLED ? 'var(--ds-blue-5)' : null}
                  />
                ),
              }}
              id={id}
              readOnly={readOnly}
              type={actualType}
              className={cls({ error: meta.error && meta.touched })}
              onChange={handleChange}
              value={value}
              defaultValue={defaultValue}
              placeholder={placeholder}
              disabled={disabled}
              name={name}
              onFocus={onFocus}
              suffix={suffixIcon}
              prefix={LeftIcon && <LeftIcon width="16px" height="16px" />}
              autoFocus={autoFocus}
            />
          )

          return inputElement
        })()}
      </InputWrapper>
    )
  }
)

Input.displayName = 'Input'

export default withValidation(Input)
